import { MouseEvent } from "react";

import { Column, RowData, Table, TableState } from "@tanstack/react-table";

import { encodeColumnOrder } from "../../../../domain/table/codecs/columnOrderCodec";
import { encodeColumnPinning } from "../../../../domain/table/codecs/columnPinningCodec";
import { encodeColumnVisibility } from "../../../../domain/table/codecs/columnVisibilityCodec";
import { TST_BLESSED_ID_COLUMN_SELECTION } from "../consts";

interface ColumnLayoutActionsInput<T extends RowData> {
    allColumns: Array<Column<T>>;
    orderedAccessorColumns: Array<Column<T>>;
    selectionColumn: Column<T> | undefined;
    selectionInitiallyPinned: boolean;
}

export interface ColumnLayoutActions<T extends RowData> {
    reorder: (reordered: Array<Column<T>>) => void;
    togglePin: (column: Column<T>) => void;
    toggleVisibility: (column: Column<T>) => (e: MouseEvent) => void;
    unpinColumns: (columns: Array<Column<T>>) => void;
    reset: () => void;
    // Whether `reset` would actually change anything - drives the disabled
    // state of the reset control.
    canReset: boolean;
}

// Builds the handlers that mutate the column layout (pin, visibility, order,
// reset); all pin-affecting ones route through `computePinnedState`.
export function getColumnLayoutActions<T extends RowData>(
    table: Table<T>,
    columns: ColumnLayoutActionsInput<T>
): ColumnLayoutActions<T> {
    const { orderedAccessorColumns } = columns;

    const reorder = (reordered: Array<Column<T>>) => {
        const reorderedIds = reordered.map((column) => column.id);
        const idRemap = new Map(
            orderedAccessorColumns.map((column, index) => [
                column.id,
                reorderedIds[index],
            ])
        );
        const visualOrderIds = [
            ...table.getLeftLeafColumns(),
            ...table.getCenterLeafColumns(),
        ].map((column) => column.id);
        const nextFullOrder = visualOrderIds.map((id) => idRemap.get(id) ?? id);
        table.setColumnOrder(nextFullOrder);
    };

    const togglePin = (column: Column<T>) => {
        const willPin = column.getIsPinned() !== "left";
        table.setState((prev) => {
            const { left, order } = computePinnedState(
                prev,
                column,
                willPin,
                columns
            );
            // Reveal a hidden column as it pins, else an invisible cell sticks
            // to the edge.
            const columnVisibility = willPin
                ? { ...prev.columnVisibility, [column.id]: true }
                : prev.columnVisibility;
            return {
                ...prev,
                columnPinning: { ...prev.columnPinning, left },
                columnOrder: order,
                columnVisibility,
            };
        });
    };

    const toggleVisibility = (column: Column<T>) => (e: MouseEvent) => {
        // Hiding a pinned column also unpins it (a hidden sticky cell makes no
        // sense); every other case defers to the default toggle.
        const shouldUnpinOnHide =
            column.getIsPinned() === "left" && column.getCanHide();
        if (!shouldUnpinOnHide) {
            column.getToggleVisibilityHandler()(e);
            return;
        }
        table.setState((prev) => {
            const { left, order } = computePinnedState(
                prev,
                column,
                false,
                columns
            );
            return {
                ...prev,
                columnPinning: { ...prev.columnPinning, left },
                columnOrder: order,
                columnVisibility: {
                    ...prev.columnVisibility,
                    [column.id]: false,
                },
            };
        });
    };

    // Unpins many columns in one update, folding each through
    // `computePinnedState` to keep the group and order consistent.
    const unpinColumns = (toUnpin: Array<Column<T>>) => {
        if (toUnpin.length === 0) {
            return;
        }
        table.setState((prev) =>
            toUnpin.reduce((acc, column) => {
                const { left, order } = computePinnedState(
                    acc,
                    column,
                    false,
                    columns
                );
                return {
                    ...acc,
                    columnPinning: { ...acc.columnPinning, left },
                    columnOrder: order,
                };
            }, prev)
        );
    };

    // Resets order, visibility, and pinning in one updater, so persistence
    // emits a single URL update with no per-slice race.
    const reset = () => {
        table.setState((prev) => ({
            ...prev,
            columnOrder: table.initialState.columnOrder ?? [],
            columnVisibility: table.initialState.columnVisibility ?? {},
            columnPinning: table.initialState.columnPinning ?? {},
        }));
    };

    const canReset = isLayoutResettable(table.getState(), table.initialState);

    return {
        reorder,
        togglePin,
        toggleVisibility,
        unpinColumns,
        reset,
        canReset,
    };
}

// A slice is dirty when it would produce a non-default URL param, so we reuse
// the persistence codecs: each `encode*` returns `null` if the slice matches
// its default. This stays in lockstep with persistence and handles the tricky
// cases (empty `columnOrder` means "default", all-visible means "default").
export function isLayoutResettable(
    state: TableState,
    initialState: TableState
): boolean {
    const defaultOrder = initialState.columnOrder ?? [];
    const orderChanged =
        encodeColumnOrder(state.columnOrder ?? [], defaultOrder) !== null;
    const visibilityChanged =
        encodeColumnVisibility(
            state.columnVisibility ?? {},
            defaultOrder,
            initialState.columnVisibility
        ) !== null;
    const pinningChanged =
        encodeColumnPinning(
            state.columnPinning ?? { left: [], right: [] },
            defaultOrder,
            initialState.columnPinning
        ) !== null;

    return orderChanged || visibilityChanged || pinningChanged;
}

export interface PinnedStateContext<T extends RowData> {
    allColumns: Array<Column<T>>;
    selectionColumn: Column<T> | undefined;
    selectionInitiallyPinned: boolean;
}

// Recomputes the pinned group and full order for one pin/unpin. Pure, so it can
// fold over a draft state across several toggles. Pinning appends to the left
// group (rightmost pin); unpinning drops to the front of the center group.
export function computePinnedState<T extends RowData>(
    prev: TableState,
    column: Column<T>,
    willPin: boolean,
    {
        allColumns,
        selectionColumn,
        selectionInitiallyPinned,
    }: PinnedStateContext<T>
) {
    const prevLeft = prev.columnPinning.left ?? [];
    const toggledLeft = willPin
        ? [...prevLeft.filter((id) => id !== column.id), column.id]
        : prevLeft.filter((id) => id !== column.id);

    // Pin the selection column only when pinned by default or another column is
    // pinned; it always sits first in the group.
    const otherPinnedIds = toggledLeft.filter(
        (id) => id !== TST_BLESSED_ID_COLUMN_SELECTION
    );
    const shouldPinSelection =
        selectionColumn !== undefined &&
        selectionColumn.getCanPin() &&
        (selectionInitiallyPinned || otherPinnedIds.length > 0);
    const left = shouldPinSelection
        ? [TST_BLESSED_ID_COLUMN_SELECTION, ...otherPinnedIds]
        : otherPinnedIds;

    // Just-unpinned column leads the center group; selection column stays first
    // overall, pinned or not.
    const justUnpinnedIds = willPin ? [] : [column.id];
    const remainingIds = allColumns
        .map((c) => c.id)
        .filter((id) => !left.includes(id) && !justUnpinnedIds.includes(id));
    const rawOrder = [...left, ...justUnpinnedIds, ...remainingIds];
    const order =
        selectionColumn !== undefined
            ? [
                  TST_BLESSED_ID_COLUMN_SELECTION,
                  ...rawOrder.filter(
                      (id) => id !== TST_BLESSED_ID_COLUMN_SELECTION
                  ),
              ]
            : rawOrder;

    return { left, order };
}
